// ─────────────────────────────────────────────────────────────
// Measurement Protocol — NESTED MP TABLE
// (the list of MPs shown under an expanded Test Overview row)
//
// Moved verbatim out of the original monolithic MPCreationIndex.js.
// No UI changes here — the Edit-icon expand bug is fixed inside the
// shared `Btn` component (see shared/mpShared.js), so this file didn't
// need to change to pick up that fix.
// ─────────────────────────────────────────────────────────────
import { Fragment, useMemo } from "react";
import {
  BRAND,
  Badge,
  Btn,
  ExpandToggle,
  ColSearch,
  TH,
  TD,
  Empty,
} from "./shared/mpShared";
import MPExpandedRow from "./MPExpandedRow";
import { hasPrivilege, PRIVILEGES } from "../../Common/UIComponents";

// ─────────────────────────────────────────────────────────────
// NESTED MP TABLE
// ─────────────────────────────────────────────────────────────
function NestedMPTable({
  testRow,
  mpRows,
  expandedMPID,
  onToggleMP,
  mpSrch,
  onSearchChange,
  onSaved,
  onDelete,
  onDuplicate,
  refreshKey = 0,
}) {
  const filteredMPRows = useMemo(
    () =>
      mpRows?.filter((r) =>
        Object.entries(mpSrch).every(([k, v]) => {
          if (!v) return true;
          const m = {
            mpNo: r.MPNo,
            testName: r.TestName,
            facility: r.FacilityName,
            startDate: r.StartDate,
            endDate: r.EndDate,
            dutRef: r.DUTRefNo,
            instrNo: r.InstrumentNo,
            ProjectName: r.ProjectName,
            status: r.Status,
          };
          return (m[k] ?? "")
            .toString()
            .toLowerCase()
            .includes(v.toLowerCase());
        }),
      ) ?? [],
    [mpRows, mpSrch],
  );

  return (
    <div className="rounded-xl border border-[#e5e9e9] bg-white overflow-hidden w-full max-w-full min-w-0">
      <div className="px-4 py-2 border-b border-[#e5e9e9] flex items-center gap-2 bg-[#f7fafa]">
        <span
          className="material-symbols-outlined text-[15px]"
          style={{ color: BRAND }}
        >
          list_alt
        </span>
        <p className="text-[12px] font-extrabold text-[#181c1d]">
          Measurement Protocol
        </p>
        <span className="text-[11px] text-[#9eabac] font-medium">
          {filteredMPRows?.length} record
          {filteredMPRows?.length !== 1 ? "s" : ""} for{" "}
          <strong className="text-[#08757C]">{testRow.TestTypeName}</strong>
        </span>
      </div>

      <div className="overflow-x-auto w-full max-w-full">
        <table className="w-full min-w-[980px] border-collapse text-[11.5px]">
          <thead>
            <tr>
              <TH w={40}></TH>
              <TH>MP No</TH>
              <TH>Test Name</TH>
              <TH>Facility</TH>
              <TH>Start Date</TH>
              <TH>End Date</TH>
              <TH>DUT Ref No</TH>
              <TH>Instrument No</TH>
              <TH>Project Name</TH>
              <TH>Status</TH>
              {hasPrivilege(PRIVILEGES.MP_EDIT) && <TH>Options</TH>}
            </tr>
            <tr className="bg-white border-b border-[#ebeeee]">
              <td className="px-2 py-1.5" />
              {[
                "mpNo",
                "testName",
                "facility",
                "startDate",
                "endDate",
                "dutRef",
                "instrNo",
                "ProjectName",
                "status",
              ]?.map((k) => (
                <td key={k} className="px-2 py-1.5">
                  <ColSearch
                    value={mpSrch[k] ?? ""}
                    onChange={(v) => onSearchChange(k, v)}
                  />
                </td>
              ))}
              <td className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {filteredMPRows?.length === 0 ? (
              <tr>
                <td colSpan={11}>
                  <Empty
                    icon="assignment"
                    msg={`No MPs found for "${testRow.TestTypeName}"`}
                    sub="Click MP New above to add one"
                  />
                </td>
              </tr>
            ) : (
              filteredMPRows?.map((mp, idx) => {
                const isOpen = expandedMPID === mp.MPID;
                const canDel = mp.Status === "Created";
                return (
                  <Fragment key={`mp-frag-${mp.MPID}`}>
                    <tr
                      className={`transition-all cursor-pointer
                        ${isOpen ? "bg-[#eef8f8]" : idx % 2 === 0 ? "bg-white hover:bg-[#f7fafa]" : "bg-[#fafcfc] hover:bg-[#f7fafa]"}`}
                      onClick={() => onToggleMP(mp.MPID)}
                    >
                      <td className="px-3 py-2 border-b border-[#f1f4f4] w-11">
                        <ExpandToggle
                          open={isOpen}
                          onClick={() => onToggleMP(mp.MPID)}
                        />
                      </td>
                      <TD>
                        <span
                          className="font-semibold"
                          style={{ color: BRAND }}
                        >
                          {mp.MPNo ?? "—"}
                        </span>
                      </TD>
                      <TD>{mp.TestName ?? "—"}</TD>
                      <TD>{mp.FacilityName ?? "—"}</TD>
                      <TD>{mp.StartDate ?? "—"}</TD>
                      <TD>{mp.EndDate ?? "—"}</TD>
                      <TD
                        style={{
                          maxWidth: 140,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span title={mp.DUTRefNo ?? ""}>
                          {mp.DUTRefNo ?? "—"}
                        </span>
                      </TD>
                      <TD
                        style={{
                          maxWidth: 140,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span title={mp.InstrumentNo ?? ""}>
                          {mp.InstrumentNo ?? "—"}
                        </span>
                      </TD>
                      <TD
                        style={{
                          maxWidth: 140,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span title={mp.ProjectName ?? ""}>
                          {mp.ProjectName ?? "—"}
                        </span>
                      </TD>
                      <TD>
                        <Badge status={mp.Status} />
                      </TD>
                      <td className="px-3 py-2 border-b border-[#f1f4f4]">
                        <div className="flex items-center gap-1.5">
                          {hasPrivilege(PRIVILEGES.MP_EDIT) && (
                            <Btn
                              icon="edit"
                              onClick={() => onToggleMP(mp.MPID)}
                              title="Edit"
                            />
                          )}
                          {hasPrivilege(PRIVILEGES.MP_EDIT) && (
                            <Btn
                              icon="content_copy"
                              onClick={() => onDuplicate(mp)}
                              title="Duplicate MP"
                            />
                          )}

                          {hasPrivilege(PRIVILEGES.MP_DELETE) && canDel && (
                            <Btn
                              icon="delete"
                              onClick={() => onDelete(mp)}
                              title="Delete"
                              danger
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={11} className="p-0 border-0">
                          <MPExpandedRow
                            key={`mp-editor-${mp.MPID}-${refreshKey}`}
                            mp={mp}
                            onSaved={onSaved}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────

export default NestedMPTable;
export { NestedMPTable };
